import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  IconButton,
  Box,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { MAP_ITEM_OPTIONS } from '../Data/MapData';
import { getBaseLabel } from '@jumbo/utils/mapHelpers';

const MapFeatureDialog = ({
  visible,
  onClose,
  onSave,
  onDelete,
  type,
  feature,
  setFeature,
  getAreaOrDistance,
  mapData,
  selectedFeature,
  boundaryAlreadyExists,
}) => {
  const mappedOptions = useMemo(() => {
    return MAP_ITEM_OPTIONS[type].map(o => (typeof o === 'string' ? { mapLabel: o, hexColor: 'black' } : o));
  }, [feature, type]);

  const [filteredOptions, setFilteredOptions] = useState(mappedOptions);
  const [displayName, setDisplayName] = useState('');

  useEffect(() => {
    setDisplayName(feature?.displayName || '');
  }, [feature]);

  useEffect(() => {
    const filterOptions = async () => {
      if (type === 'polygon') {
        const boundaryExists = mapData
          ?.filter(item => item.mapType === 'polygon')
          ?.some(item => (item?.mapLabel || '').toLowerCase().includes('bound'));

        let filtered = mappedOptions;

        if (boundaryExists) {
          filtered = filtered.filter(item => !item.mapLabel.toLowerCase().includes('bound'));
        }

        setFilteredOptions(filtered);
        return;
      }
      setFilteredOptions(mappedOptions);
    };

    filterOptions();
  }, [type, mappedOptions, boundaryAlreadyExists]);

  const handleOptionSelect = value => {
    console.log('Current feature:', feature);
    console.log('Selected mapLabel:', value);
    if (!feature) return;

    const selected = filteredOptions.find(opt => opt.mapLabel === value);
    console.log('Matching option:', selected);
    if (!selected) return;

    setFeature({
      ...feature,
      mapLabel: selected.mapLabel,
      hexColor: selected.hexColor,
    });
  };

  const getNextAvailableLabel = (existingLabels, baseLabel) => {
    let i = 0;
    let candidate = baseLabel;
    const existingSet = new Set(existingLabels);

    while (existingSet.has(candidate)) {
      i += 1;
      candidate = `${baseLabel} ${i}`;
    }

    return candidate;
  };

  const handleSaveWithLabeling = () => {
    let base;
    if (type === 'pin') {
      base = feature?.latitude && feature?.longitude ? feature : selectedFeature;
    } else {
      base = feature?.coords?.length ? feature : selectedFeature;
    }

    if (!base || !base.mapLabel || !type) return;

    const baseLabel = getBaseLabel(base.mapLabel);
    let newLabel = base.mapLabel;

    const allExistingLabels = mapData?.filter(item => item.mapType === type)?.map(item => item.mapLabel) || [];

    newLabel = getNextAvailableLabel(allExistingLabels, baseLabel);

    const updatedBase = {
      ...base,
      mapLabel: newLabel,
      displayName: displayName?.trim() || newLabel,
    };
    if (type === 'pin') {
      onSave(updatedBase, selectedFeature?.mapLabel);
      return;
    }
    if ((type === 'polygon' || type === 'line') && Array.isArray(base.coords)) {
      const expandedFeatures = {
        ...updatedBase,
        coords: [...base.coords],
      };

      onSave(expandedFeatures, selectedFeature?.mapLabel);
      return;
    }

    // onSave(updatedBase); // fallback
  };

  const handleDelete = async () => {
    if (!feature) return;

    if (type === 'pin') {
      await onDelete(feature); // delete single pin by ID
    } else if (type === 'polygon' || type === 'line') {
      const itemsToDelete = mapData.filter(item => item.mapType === type && item.mapLabel === feature.mapLabel);

      await Promise.all(itemsToDelete.map(item => onDelete(item)));
    }

    onClose(); // Close dialog after deletion
  };
  const getContainer = () => {
    const fullscreenElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (fullscreenElement) {
      // Find our map container inside the fullscreen element
      return fullscreenElement.querySelector('#map-fullscreen-container') || fullscreenElement;
    }
    return undefined; // Default to body
  };
  return (
    <Dialog
      open={visible}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      disableAutoFocus
      disableEnforceFocus
      style={{ position: 'absolute' }}
      container={getContainer}
      disablePortal={false}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>{feature?.mapLabel ? 'Edit Info' : 'Add Info'}</span>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {getAreaOrDistance && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {getAreaOrDistance()}
          </Typography>
        )}

        <TextField
          select
          label="Select from options"
          value={getBaseLabel(feature?.mapLabel) || ''}
          onChange={e => handleOptionSelect(e.target.value)}
          fullWidth
          size="small"
          SelectProps={{
            MenuProps: {
              container: getContainer(),
              disablePortal: false,
              style: { position: 'absolute' },
            },
          }}
          sx={{ mb: 2 }}>
          {filteredOptions.map(opt => (
            <MenuItem key={opt.mapLabel} value={opt.mapLabel}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    backgroundColor: opt.hexColor,
                    border: '1px solid #ccc',
                  }}
                />
                {opt.mapLabel}
              </Box>
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Display Name"
          placeholder="Enter a display name"
          value={displayName}
          onChange={e => setDisplayName(e.target.value)}
          fullWidth
          size="small"
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSaveWithLabeling} variant="contained">
          Save
        </Button>
      </DialogActions>

      {selectedFeature && (
        <Box sx={{ textAlign: 'center', p: 1 }}>
          <Button onClick={handleDelete} color="error" size="small">
            Delete {feature?.mapLabel || 'this item'}?
          </Button>
        </Box>
      )}
    </Dialog>
  );
};

export default MapFeatureDialog;
